import { formatDate } from '../../../helpers/UtilityHelper';
import { clientOrderIdClause } from '../../orders/list2/ClientOrderIdText';
import { TOKEN_COLOR } from './tokens';

export const DEFAULT_DATE_RANGE_DAYS = 90;

export const getDefaultDateRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - DEFAULT_DATE_RANGE_DAYS);
    return { fromDate: from, toDate: to };
};

export const fmt = (d) => (d ? formatDate(d, 'yyyy-MM-dd') : null);

export const ABD_BASE_FILTER = [
    { field: 'shipmentType', operator: 'eq', value: 'B2B' },
    { field: 'appointdeliveryRequired', operator: 'eq', value: true },
];

/**
 * ABD / Non-ABD scope on the appointment flag. Non-ABD uses `ne true` (as the Admin Panel) so older
 * orders written before `appointdeliveryRequired` existed (field missing) still count as Non-ABD.
 */
export const abdFlagFilter = (abd) => (abd
    ? { field: 'appointdeliveryRequired', operator: 'eq', value: true }
    : { field: 'appointdeliveryRequired', operator: 'ne', value: true });

export const CLOSURE_CATEGORIES = Object.freeze({
    PROZO: 'PROZO_ATTRIBUTABLE',
    NON_PROZO: 'NON_PROZO_ATTRIBUTABLE',
});

const TERMINAL_OR_FAIL_STATUSES = Object.freeze([
    'CANCELLED_ORDER',
    'DELIVERED',
    'RTO_DELIVERED',
    'LOST',
    'DISPOSED_OFF',
    'DAMAGED',
]);

export const computeTotalMissed = (kpi = {}) =>
    (kpi.miss1 || 0) + (kpi.miss2 || 0) + (kpi.miss3plus || 0);

export const ABD_INCLUDES = [
    'appointmentDeliveryDate',
    'appointmentDelivery',
    'appointmentDeliveryData',
    'appointmentDeliveryHistory',
    'appointmentDeliveryHistoryCount',
    'edd',
    'orderDate',
    'id',
    'reference',
    'clientOrderId',
    'invoiceValue',
    'pods',
    'shippingLabelId',
    'awbRegisteredDate',
    'merchantPricing.zone',
    'merchantPricing.price',
    'merchantDetail',
    'courierDetail',
    'pickupDetails.from_pincode',
    'pickupDetails.from_city',
    'deliveryDetails.to_pincode',
    'deliveryDetails.to_state',
    'deliveryDetails.to_city',
    'channelName',
    'orderStatus',
    'shipmentDetail',
    'paymentMode',
    'awbNumber',
    'referenceNumber',
    'additionalData.soldToPartyName',
    'soldToPartyDetails.name',
    'soldToPartyDetails.address',
    'soldToPartyDetails.city',
    'soldToPartyDetails.state',
    'soldToPartyDetails.pincode',
    'appointdeliveryRequired',
    'terminalStatus',
].join(',');

export const PILL_KEYS = {
    ALL: 'all',
    ACTIVE: 'active',
    MISS_1: 'miss1',
    MISS_2: 'miss2',
    MISS_3_PLUS: 'miss3plus',
    NEEDS_SCHEDULING: 'needsScheduling',
    DELIVERED: 'delivered',
    PROZO_FAULT: 'prozoFault',
    NON_PROZO: 'nonProzo',
    EVENTUALLY_DELIVERED: 'eventuallyDelivered',
    PROZO_FAULT_PENDING: 'prozoFaultPending',
};

const NOT_TERMINAL_FILTERS = [
    { field: 'terminalStatus', operator: 'exists', value: false },
    { field: 'orderStatus', operator: 'nin', value: TERMINAL_OR_FAIL_STATUSES },
];

export const buildPillFilter = (key) => {
    switch (key) {
        case PILL_KEYS.ACTIVE:
            return [
                { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: true },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.MISS_1:
            return [
                { field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: 1 },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.MISS_2:
            return [
                { field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: 2 },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.MISS_3_PLUS:
            return [
                { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 3 },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.NEEDS_SCHEDULING:
            return [
                { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.DELIVERED:
            return [{ field: 'orderStatus', operator: 'eq', value: 'DELIVERED' }];
        // Category of the LATEST miss only (as the Admin Panel), so a shipment counts under one of the two.
        case PILL_KEYS.PROZO_FAULT:
            return [
                { field: 'appointmentDeliveryHistory.closureCategory', operator: 'lasteq', value: CLOSURE_CATEGORIES.PROZO },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.NON_PROZO:
            return [
                { field: 'appointmentDeliveryHistory.closureCategory', operator: 'lasteq', value: CLOSURE_CATEGORIES.NON_PROZO },
                ...NOT_TERMINAL_FILTERS,
            ];
        case PILL_KEYS.EVENTUALLY_DELIVERED:
            return [
                { field: 'orderStatus', operator: 'eq', value: 'DELIVERED' },
                { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 },
            ];
        case PILL_KEYS.PROZO_FAULT_PENDING:
            return [
                { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
                ...NOT_TERMINAL_FILTERS,
                { field: 'appointmentDeliveryHistory.closureCategory', operator: 'eq', value: 'PROZO_ATTRIBUTABLE' },
            ];
        case PILL_KEYS.ALL:
        default:
            return [];
    }
};

export const DOCUMENTS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'UPLOADED', label: 'Uploaded' },
    { value: 'NOT_UPLOADED', label: 'Not uploaded' },
];

// Same rule as the Admin Panel's "Documents" filter. Where a shipment's documents can live: uploaded before
// any appointment (additionalDocuments), on the active appointment, or on an earlier (closed) one. Any of
// these set = "uploaded". `extraDocs.0` = the extra-docs list is non-empty.
const DOC_PATHS = ['invoiceS3Key', 'ewayBillS3Key', 'poCopyS3Key', 'extraDocs.0'].map((f) => `additionalDocuments.${f}`);
const APPT_DOC_KEYS = ['appointmentDeliveryInvoiceS3Key', 'appointmentDeliveryEwayBillS3Key', 'appointmentDeliveryPOCopyS3Key', 'extraDocs.0'];
const DOCUMENT_FIELDS = [
    ...DOC_PATHS,
    ...APPT_DOC_KEYS.map((f) => `appointmentDeliveryData.${f}`),
    ...APPT_DOC_KEYS.map((f) => `appointmentDeliveryHistory.${f}`),
];

export const documentsFilter = (documents) => {
    if (documents === 'UPLOADED') {
        // Server operator: one condition, at least one of the fields set (combines with all other filters).
        return [{ field: 'documents', operator: 'anynotnull', value: DOCUMENT_FIELDS }];
    }
    if (documents === 'NOT_UPLOADED') {
        return DOCUMENT_FIELDS.map((field) => ({ field, operator: 'eq', value: null }));
    }
    return [];
};

export const MISS_CATEGORY_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'PROZO_ATTRIBUTABLE', label: 'Prozo Attributable' },
    { value: 'NON_PROZO_ATTRIBUTABLE', label: 'Non-Prozo Attributable' },
];

export const BUCKET_KEYS = [
    PILL_KEYS.ALL,
    PILL_KEYS.ACTIVE,
    PILL_KEYS.MISS_1,
    PILL_KEYS.MISS_2,
    PILL_KEYS.MISS_3_PLUS,
    PILL_KEYS.NEEDS_SCHEDULING,
    PILL_KEYS.DELIVERED,
    PILL_KEYS.PROZO_FAULT,
    PILL_KEYS.NON_PROZO,
    PILL_KEYS.EVENTUALLY_DELIVERED,
    PILL_KEYS.PROZO_FAULT_PENDING,
];

/**
 * Builds the andfilter baseline for both /order/search (grid) and
 * /appointment_delivery/dashboard so counts and rows always match.
 *
 * merchantId is always injected automatically by the calling hook — merchants
 * only see their own shipments and there is no merchant selector in the UI.
 */
export const buildBaselineAndFilter = (filters = {}, merchantId = '', appointmentRequired = true) => {
    // appointmentRequired = false renders the same pages scoped to Non-ABD shipments.
    const arr = [
        { field: 'shipmentType', operator: 'eq', value: 'B2B' },
        abdFlagFilter(appointmentRequired),
    ];
    if (merchantId) {
        arr.push({ field: 'merchantDetail.key', operator: 'eq', value: merchantId });
    }
    if (filters.fromDate) {
        arr.push({ field: 'awbRegisteredDate', operator: 'gte', value: `Date('${fmt(filters.fromDate)} 00:00:00')` });
    }
    if (filters.toDate) {
        arr.push({ field: 'awbRegisteredDate', operator: 'lte', value: `Date('${fmt(filters.toDate)} 23:59:59')` });
    }
    if (filters.courier) {
        arr.push({ field: 'courier.$id', operator: 'eq', value: `ObjectId('${filters.courier}')` });
    }
    if (filters.city) {
        arr.push({ field: 'deliveryDetails.to_city', operator: 'eq', value: filters.city });
    }
    if (filters.state) {
        arr.push({ field: 'deliveryDetails.to_state', operator: 'eq', value: filters.state });
    }
    if (filters.awb) {
        arr.push({ field: 'awbNumber', operator: 'eq', value: filters.awb });
    }
    const clientOrderFilter = clientOrderIdClause(filters);
    if (clientOrderFilter) {
        arr.push(clientOrderFilter);
    }
    if (filters.missCategory) {
        arr.push({ field: 'appointmentDeliveryHistory.closureCategory', operator: 'eq', value: filters.missCategory });
        arr.push({ field: 'terminalStatus', operator: 'exists', value: false });
        arr.push({ field: 'orderStatus', operator: 'nin', value: TERMINAL_OR_FAIL_STATUSES });
    }
    arr.push(...documentsFilter(filters.documents));
    if (filters.attempts) {
        const n = parseInt(filters.attempts, 10);
        if (n === 4) {
            arr.push({ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 4 });
        } else if (!Number.isNaN(n)) {
            arr.push({ field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: n });
        }
    }
    return arr;
};

const dateValue = (d) => `Date('${formatDate(d, 'yyyy-MM-dd HH:mm:ss')}')`;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const daysBefore = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - n);

// The latest miss = last entry of appointmentDeliveryHistory.
export const lastMiss = (row) => {
    const h = row?.appointmentDeliveryHistory;
    return Array.isArray(h) && h.length ? h[h.length - 1] : null;
};

// Overdue appointments (Admin Panel rule): still open, never marked missed, on any day after the appointment
// date, i.e. the slot ended before the start of today. Delivered / RTO / cancelled shipments are left out.
export const buildOverdueFilter = (now) => [
    { field: 'appointmentDelivery', operator: 'eq', value: true },
    { field: 'appointmentDeliveryData.appointmentDeliveryEndTime', operator: 'lt', value: dateValue(startOfDay(now)) },
    ...NOT_TERMINAL_FILTERS,
];

// Missed with a reason, no new appointment booked yet, still open.
export const NO_NEW_DATE_FILTER = [
    { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 },
    { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
    ...NOT_TERMINAL_FILTERS,
];

// ABD on but the first appointment is not booked yet: no appointment, never missed, still open (Admin Panel
// "Waiting for 1st appt").
export const WAITING_FIRST_APPOINTMENT_FILTER = [
    { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', operator: 'exists', value: false },
    { field: 'appointmentDeliveryHistory.0', operator: 'exists', value: false },
    ...NOT_TERMINAL_FILTERS,
];

// Missed at least once and still open (rebooked or not).
export const OPEN_MISSED_FILTER = [
    { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 },
    ...NOT_TERMINAL_FILTERS,
];

// Days since the latest missed appointment date. maxDays null = no upper limit. Hints and colours as the Admin Panel.
export const NO_NEW_DATE_AGE_BANDS = Object.freeze([
    { key: 'age1', label: '0 – 6 days', minDays: 0, maxDays: 6, hint: 'Recent', kpiColor: 'grey', color: '#78909c', bg: '#eceff1' },
    { key: 'age2', label: '7 – 14 days', minDays: 7, maxDays: 14, hint: 'Follow up', kpiColor: 'amber', color: TOKEN_COLOR.warning, bg: TOKEN_COLOR.softYellowBg },
    { key: 'age3', label: '15 – 22 days', minDays: 15, maxDays: 22, hint: 'Escalate', kpiColor: 'red', color: '#e53935', bg: TOKEN_COLOR.dangerBg },
    { key: 'age4', label: '23+ days', minDays: 23, maxDays: null, hint: 'Call today', kpiColor: 'red', color: '#8e0000', bg: TOKEN_COLOR.dangerBg },
]);
export const ageBandOf = (days) => NO_NEW_DATE_AGE_BANDS.find((b) => days >= b.minDays && (b.maxDays == null || days <= b.maxDays));

export const ageBandFilter = (band, now) => {
    const today = startOfDay(now);
    const arr = [{ field: 'appointmentDeliveryHistory.appointmentDeliveryStartTime', operator: 'lastlt', value: dateValue(daysBefore(today, band.minDays - 1)) }];
    if (band.maxDays != null) {
        arr.push({ field: 'appointmentDeliveryHistory.appointmentDeliveryStartTime', operator: 'lastgte', value: dateValue(daysBefore(today, band.maxDays)) });
    }
    return arr;
};

export const buildBaselineOrFilter = (filters = {}) => {
    const arr = [];
    if (filters.reference) {
        arr.push({ field: 'reference', operator: 'like', value: `${filters.reference}.*` });
        arr.push({ field: 'shippingLabelId', operator: 'like', value: `${filters.reference}.*` });
    }
    return arr;
};

export const ATTEMPT_OPTIONS = [
    { value: '', label: 'All' },
    { value: '1', label: '1 attempt' },
    { value: '2', label: '2 attempts' },
    { value: '3', label: '3 attempts' },
    { value: '4', label: '4+ attempts' },
];
